import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { BifiStatsDTO } from '@bifeai/shared';
import { BarChart, Card, CardBody, CardHeader, EmptyState, Kpi, PageHeader, Skeleton } from '@bifeai/ui';
import { MessageCircle, MessagesSquare, Moon, Percent, ShoppingBasket, Sparkles, Store, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { todayIso } from '../../lib/units';
import { PeriodPicker, type Period } from '../DashboardPage';

const PERIOD_KEY = 'bifeai.bifi-stats.period';
const DAYS_OF: Record<string, number> = { today: 1, '7d': 7, '15d': 15, '30d': 30, '60d': 60, '90d': 90, '6m': 182, '1y': 365 };
const num = (v: number) => v.toLocaleString('es-AR');
/** Números grandes abreviados para que entren en la tarjeta (1,2 M). */
const big = (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} M` : num(v));
const bigMoney = (cents: number) =>
  cents >= 10_000_000_000 ? `$ ${(cents / 100_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} M` : formatMoney(cents);

function loadPeriod(): Period {
  try {
    const p = JSON.parse(localStorage.getItem(PERIOD_KEY) ?? 'null') as Period | null;
    if (p?.range) return p;
  } catch {
    /* sin almacenamiento */
  }
  return { range: '30d', from: todayIso(), to: todayIso() };
}

/** Desde / hasta del período elegido (días de Argentina). */
function rangeOf(p: Period): { from: string; to: string } {
  if (p.range === 'custom') return { from: p.from, to: p.to };
  const to = todayIso();
  const from = new Date(new Date(`${to}T12:00:00Z`).getTime() - ((DAYS_OF[p.range] ?? 30) - 1) * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

/** Estadísticas de bifi: lo que contestó, lo que vendió y lo que sumó, para Solo bifi y para los planes con bifi. */
export function BifiStatsPage() {
  const { selectedBranchId } = useSession();
  const [period, setPeriod] = useState<Period>(loadPeriod);
  useEffect(() => {
    try {
      localStorage.setItem(PERIOD_KEY, JSON.stringify(period));
    } catch {
      /* ignorar */
    }
  }, [period]);
  const r = rangeOf(period);
  const q = useQuery({
    queryKey: ['bifi-stats', selectedBranchId, r.from, r.to],
    queryFn: () => api.get<BifiStatsDTO>(`/orders-chat/stats?from=${r.from}&to=${r.to}`),
    placeholderData: keepPreviousData,
  });
  const d = q.data;

  return (
    <>
      <PageHeader
        title="Estadísticas de bifi"
        subtitle="Lo que contestó, lo que vendió y lo que sumó tu agente de WhatsApp. Elegí el período y la sucursal arriba."
      />
      <PeriodPicker value={period} onChange={setPeriod} />
      {!d ? (
        <Skeleton style={{ height: 320 }} />
      ) : (
        <>
          <div className="bf-kpi-grid">
            <Kpi
              label="Chats de clientes"
              tone="celeste"
              icon={<MessagesSquare />}
              value={big(d.chats.total)}
              delta={`${big(d.chats.customers)} clientes distintos`}
              deltaMuted
            />
            <Kpi
              label="Pedidos que tomó"
              tone="green"
              icon={<ShoppingBasket />}
              value={big(d.orders.count)}
              delta={`Ticket promedio ${formatMoney(d.orders.avgTicketCents)}`}
              deltaMuted
            />
            <Kpi
              label="Vendido por WhatsApp"
              tone="amber"
              icon={<TrendingUp />}
              value={bigMoney(d.orders.amountCents)}
              delta="Pedidos confirmados"
              deltaMuted
            />
            <Kpi
              label="Conversión"
              tone="red"
              icon={<Percent />}
              value={d.conversionPct === null ? '—' : `${d.conversionPct.toLocaleString('es-AR')} %`}
              delta="Chats que terminaron en pedido"
              deltaMuted
            />
          </div>
          <div className="bf-kpi-grid">
            <Kpi
              label="Venta cruzada"
              tone="green"
              icon={<Sparkles />}
              value={bigMoney(d.crossSell.amountCents)}
              delta={`Aceptaron ${num(d.crossSell.accepted)} de ${num(d.crossSell.offered)} ofertas`}
              deltaMuted
            />
            <Kpi
              label="Chats con el local cerrado"
              tone="celeste"
              icon={<Moon />}
              value={big(d.chats.outOfHours)}
              delta="Atendidos fuera de horario"
              deltaMuted
            />
            <Kpi
              label="Pedidos con el local cerrado"
              tone="amber"
              icon={<Store />}
              value={big(d.ordersOutOfHours)}
              delta="Ventas que no se perdieron"
              deltaMuted
            />
            <Kpi
              label="Mensajes que contestó"
              tone="red"
              icon={<MessageCircle />}
              value={big(d.messages.byBifi)}
              delta={`De ${big(d.messages.received)} recibidos · ${big(d.messages.audios)} audios`}
              deltaMuted
            />
          </div>

          <Card>
            <CardHeader title="Vendido por día" subtitle={`${formatDate(d.period.from)} al ${formatDate(d.period.to)}.`} />
            <CardBody>
              <BarChart
                ariaLabel="Vendido por día"
                data={d.byDay.map((x) => ({ key: x.date, label: shortDay(x.date), value: x.amountCents }))}
                tip={(i) => {
                  const x = d.byDay[i]!;
                  return { title: formatDate(x.date), lines: [formatMoney(x.amountCents), `${num(x.orders)} pedidos`, `${num(x.chats)} chats`] };
                }}
              />
            </CardBody>
          </Card>

          <div className="bf-dash-grid">
            <Card>
              <CardHeader title="Chats y clientes" subtitle="Un chat es cada vez que un cliente te escribe después de 12 horas sin hablar." />
              <CardBody>
                <Rows
                  rows={[
                    ['Chats de clientes', num(d.chats.total)],
                    ['Clientes distintos que escribieron', num(d.chats.customers)],
                    ['Chats de gente que escribía por primera vez', num(d.chats.firstTime)],
                    ['Los resolvió bifi solo', num(d.chats.solo)],
                    ['Intervino alguien del equipo', num(d.chats.withTeam)],
                    ['Llegaron por anuncios', num(d.chats.fromAds)],
                    ['Audios atendidos', num(d.messages.audios)],
                    ['Pedidos de clientes nuevos', num(d.customers.new)],
                    ['Pedidos de clientes que vuelven', num(d.customers.returning)],
                    ['Pagados con el link de Mercado Pago', `${num(d.paidLink.count)} · ${formatMoney(d.paidLink.amountCents)}`],
                    ['Pedidos cancelados', num(d.orders.canceled)],
                  ]}
                />
              </CardBody>
            </Card>
            <Card>
              <CardHeader title="Lo que cuesta y lo que deja" subtitle="La IA la pagás a Anthropic con tu cuenta; los mensajes con costo, a Meta." />
              <CardBody>
                <Rows
                  rows={[
                    ['Costo estimado de la IA', `USD ${d.cost.aiUsd.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`],
                    ['Mensajes con costo de Meta', num(d.cost.billableMessages)],
                    ['Vendido por WhatsApp', formatMoney(d.orders.amountCents)],
                    ['Sumado con venta cruzada', formatMoney(d.crossSell.amountCents)],
                  ]}
                />
                <p className="bf-cell-sub" style={{ marginTop: 12 }}>
                  El costo de la IA es una estimación con los precios de Anthropic que carga BifeAI. Lo exacto lo ves en tu cuenta de Anthropic.
                </p>
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader title="A qué hora te escriben" subtitle="Chats que empezaron en cada hora del día." />
            <CardBody>
              <BarChart
                ariaLabel="Chats por hora del día"
                height={160}
                data={d.byHour.map((v, h) => ({ key: String(h), label: h % 3 === 0 ? `${h} h` : '', value: v }))}
                tip={(h) => ({ title: `De ${h} a ${h + 1} h`, lines: [`${num(d.byHour[h]!)} chats`] })}
              />
            </CardBody>
          </Card>

          <div className="bf-dash-grid">
            {d.byBranch.length > 1 && (
              <Card>
                <CardHeader title="Por sucursal" />
                <CardBody>
                  <div className="bf-table-wrap">
                    <table className="bf-table">
                      <thead>
                        <tr>
                          <th>Sucursal</th>
                          <th style={{ textAlign: 'right' }}>Pedidos</th>
                          <th style={{ textAlign: 'right' }}>Vendido</th>
                          <th style={{ textAlign: 'right' }}>Venta cruzada</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.byBranch.map((b) => (
                          <tr key={b.branchId}>
                            <td>{b.name}</td>
                            <td style={{ textAlign: 'right' }}>{num(b.orders)}</td>
                            <td style={{ textAlign: 'right' }}>{formatMoney(b.amountCents)}</td>
                            <td style={{ textAlign: 'right' }}>{formatMoney(b.crossSellCents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            )}
            <Card>
              <CardHeader title="Lo más pedido por WhatsApp" />
              <CardBody>
                {d.topProducts.length === 0 ? (
                  <EmptyState icon={<ShoppingBasket />} title="Todavía no hay pedidos en este período" />
                ) : (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {d.topProducts.map((p) => (
                      <div key={p.name} className="bf-bar-row">
                        <span>{p.name}</span>
                        <span className="bf-bar">
                          <span style={{ width: `${(p.orders / Math.max(...d.topProducts.map((x) => x.orders), 1)) * 100}%` }} />
                        </span>
                        <span className="bf-cell-sub">
                          {num(p.orders)} pedidos · {formatMoney(p.amountCents)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

const dayFmt = new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'numeric' });
const shortDay = (iso: string) => dayFmt.format(new Date(`${iso}T12:00:00`));

function Rows({ rows }: { rows: [string, string][] }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span className="bf-cell-sub">{k}</span>
          <strong>{v}</strong>
        </div>
      ))}
    </div>
  );
}
